import React, { useMemo } from 'react';
import {
  FlatList,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import MaterialIcons from '@react-native-vector-icons/material-icons/static';
import type { OrderDoc } from '../../redux/backoffice/ordersSlice';
import { useThemeColors, GTFonts, withOpacity } from '../../theme';
import {
  borderWidth,
  fs,
  iconSize,
  s,
  v,
  getCurrencyDisplayFormat,
  formatMoney,
} from '../../utils/libs';
import theme from '../../theme/theme';
import AppSkeleton from '../Reusables/AppSkeleton';
import { formatDisplayDateTime } from '../../utils/formatDisplayDateTime';

type RecentTransactionsSectionProps = {
  transactions: OrderDoc[];
  loading: boolean;
  error?: string | null;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  onPressTransaction: (order: OrderDoc) => void;
  onPressViewAll: () => void;
  currencyCode?: string;
  /** When true, rows / view all / load more / scroll are disabled (e.g. during location switch). */
  interactionDisabled?: boolean;
};

const statusToColor = (status: string, colors: any) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized.includes('partial')) {
    return colors.warningDark;
  }
  if (normalized.includes('pending')) {
    return colors.infoDark;
  }
  return colors.successDark;
};

const RecentTransactionsSection = ({
  transactions,
  loading,
  error,
  hasNextPage,
  onLoadMore,
  onPressTransaction,
  onPressViewAll,
  currencyCode,
  interactionDisabled = false,
}: RecentTransactionsSectionProps) => {
  const colors = useThemeColors();
  const { height: windowHeight } = useWindowDimensions();
  const listHeight = Math.min(
    windowHeight * (transactions.length > 5 ? 0.8 : 0.4),
    750,
  );
  const styles = useMemo(() => createStyles(colors), [colors]);
  const currencyText = getCurrencyDisplayFormat(currencyCode || 'KES');

  const renderItem = ({ item }: { item: OrderDoc }) => {
    const status = String(item?.bp_order_master_order_status || 'completed');
    const statusLower = status.toLowerCase();
    const statusColor = statusToColor(status, colors);
    const isCompleted = statusLower.includes('completed');
    const isPending = statusLower.includes('pending');
    const itemCount = item?.bp_sales_products_information?.length || 0;
    const paymentInfo = item?.bp_order_master_payment_information;
    const amountDue = Number(paymentInfo?.bp_payment_amount_due || 0);
    const isFullyPaid = Boolean(paymentInfo?.bp_payment_status);
    const dueColor = colors.due || colors.secondaryDark || colors.warningDark;
    const updatedAt = item?.bp_order_master_updated_at;
    const dateTimeText = formatDisplayDateTime(updatedAt, '');

    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`Open transaction ${item.bp_order_id}`}
        disabled={interactionDisabled}
        onPress={() => onPressTransaction(item)}
        style={({ pressed, hovered }: any) => [
          styles.row,
          Platform.OS === 'web' && styles.rowWeb,
          Platform.OS === 'web' &&
            hovered &&
            !interactionDisabled &&
            styles.rowHovered,
          pressed &&
            !interactionDisabled && {
              opacity: 0.75,
              transform: [{ scale: 0.995 }],
            },
        ]}>
        <View style={[styles.statusRail, { backgroundColor: statusColor }]} />
        <View style={styles.rowBody}>
          <View style={styles.rowMain}>
            <View style={styles.leftCol}>
              <View style={styles.nameRow}>
                <Text style={styles.customerName} numberOfLines={1}>
                  {item?.bp_order_customer_information || 'Walk-in customer'}
                </Text>
                <Text style={styles.productCount}>
                  {'•'}
                  {itemCount}
                  {'\u00A0'}
                  {itemCount === 1 ? 'Item' : 'Items'}
                </Text>
              </View>
              {/* {item.bp_order_master_receipt_reference_id ? (
                <Text style={styles.receiptText} numberOfLines={1}>
                  Receipt: {item.bp_order_master_receipt_reference_id}
                </Text>
              ) : null} */}

              <View style={styles.metaRow}>
                <Text style={styles.metaText} numberOfLines={1}>
                  {dateTimeText}
                </Text>
              </View>
            </View>

            <View style={styles.rightCol}>
              <Text style={styles.amountText} numberOfLines={1}>
                {currencyText}{' '}
                {formatMoney(paymentInfo?.bp_payment_amount || 0)}
              </Text>

              <View style={styles.rightBadges}>
                <View
                  style={[
                    styles.statusChip,
                    {
                      borderColor: withOpacity(statusColor, 0.35),
                    },
                  ]}>
                  <Text style={[styles.statusText, { color: statusColor }]}>
                    {status.toUpperCase()}
                  </Text>
                </View>

                {!isCompleted && (!isPending || isFullyPaid) ? (
                  <View
                    style={[
                      styles.paymentChip,
                      {
                        borderColor: withOpacity(
                          isFullyPaid ? colors.successDark : dueColor,
                          0.35,
                        ),
                      },
                    ]}>
                    <Text
                      style={[
                        styles.paymentText,
                        {
                          color: isFullyPaid ? colors.successDark : dueColor,
                        },
                      ]}>
                      {isFullyPaid
                        ? 'PAID'
                        : `DUE ${currencyText} ${formatMoney(amountDue)}`}
                    </Text>
                  </View>
                ) : null}
              </View>
            </View>
          </View>
        </View>
      </Pressable>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.headerRow}>
        <Text style={styles.title}>Recent Transactions</Text>
        <Pressable
          onPress={onPressViewAll}
          disabled={interactionDisabled}
          style={({ pressed, hovered }: any) => [
            styles.viewAllButton,
            Platform.OS === 'web' && hovered && { opacity: 0.7 },
            pressed && { opacity: 0.7 },
          ]}>
          <Text style={styles.viewAllText}>View all</Text>
          <MaterialIcons
            name="chevron-right"
            size={iconSize(18)}
            color={styles.viewAllText.color as any}
          />
        </Pressable>
      </View>

      {/* {error ? <Text style={styles.errorText}>{error}</Text> : null} */}
      {!loading && transactions.length === 0 && !error ? (
        <Text style={styles.emptyText}>No recent transactions yet.</Text>
      ) : null}

      <View style={{ height: listHeight }}>
        <FlatList
          data={transactions}
          keyExtractor={item => String(item.bp_order_id)}
          renderItem={renderItem}
          scrollEnabled={!interactionDisabled}
          showsVerticalScrollIndicator={true}
          nestedScrollEnabled={true}
          ItemSeparatorComponent={() => <View style={{ height: v(8) }} />}
          ListFooterComponent={
            loading ? (
              <View style={styles.skeletonFooter}>
                <AppSkeleton
                  mode="recent_transactions"
                  count={transactions.length > 0 ? 2 : 5}
                />
              </View>
            ) : hasNextPage && onLoadMore ? (
              <Pressable
                onPress={onLoadMore}
                disabled={interactionDisabled}
                style={({ pressed, hovered }: any) => [
                  styles.loadMoreButton,
                  Platform.OS === 'web' && hovered && { opacity: 0.85 },
                  pressed && { opacity: 0.7 },
                ]}>
                <Text style={styles.loadMoreText}>Load more</Text>
              </Pressable>
            ) : null
          }
        />
      </View>
    </View>
  );
};

const createStyles = (colors: any) =>
  StyleSheet.create({
    container: {
      marginTop: v(10),
      backgroundColor: colors.surface,
      borderRadius: theme.borderRadius.md,
      paddingVertical: v(8),
      paddingHorizontal: s(12),
      ...(Platform.OS === 'web'
        ? { boxShadow: `0px ${v(4)}px ${s(12)}px rgba(5, 28, 88, 0.08)` }
        : theme.shadows.md),
    } as any,
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: v(6),
    },
    title: {
      fontFamily: GTFonts.semibold,
      color: colors.text,
      fontSize: fs(17),
      lineHeight: fs(20),
    },
    viewAllButton: {
      paddingVertical: v(5),
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(2),
    },
    viewAllText: {
      fontFamily: GTFonts.semibold,
      color: colors.primary,
      fontSize: fs(12),
      lineHeight: fs(14),
    },
    errorText: {
      fontFamily: GTFonts.regular,
      color: colors.error,
      fontSize: fs(12),
      lineHeight: fs(16),
      marginBottom: v(8),
    },
    skeletonFooter: {
      marginTop: v(10),
      marginBottom: v(2),
    },
    loadMoreButton: {
      alignSelf: 'center',
      marginTop: v(12),
      marginBottom: v(8),
      paddingHorizontal: s(20),
      paddingVertical: v(8),
      borderRadius: theme.borderRadius.md,
      backgroundColor: withOpacity(colors.primary, 0.12),
      borderWidth: borderWidth(1),
      borderColor: withOpacity(colors.primary, 0.3),
    },
    loadMoreText: {
      fontFamily: GTFonts.semibold,
      color: colors.primary,
      fontSize: fs(13),
      lineHeight: fs(16),
    },
    emptyText: {
      fontFamily: GTFonts.regular,
      color: colors.textSecondary,
      fontSize: fs(13),
      lineHeight: fs(18),
      textAlign: 'center',
      marginVertical: v(12),
    },
    row: {
      flexDirection: 'row',
      borderRadius: theme.borderRadius.md,
      borderWidth: borderWidth(1),
      borderColor: withOpacity(colors.text, 0.06),
      // Solid inset surface (level 2) nested inside the white card - this
      // tonal step is what makes the card read as dimensional
      backgroundColor: 'rgba(0,0,0,0.03)',
      marginBottom: v(2),
      overflow: 'hidden',
    },
    rowWeb: {
      ...(Platform.OS === 'web'
        ? ({
            cursor: 'pointer',
            transition: 'background-color 0.15s ease-out',
          } as any)
        : {}),
    },
    rowHovered: {
      backgroundColor: colors.surfaceSecondary,
    },
    statusRail: {
      width: s(5),
      alignSelf: 'stretch',
    },
    rowBody: {
      flex: 1,
      paddingHorizontal: s(10),
      paddingVertical: v(8),
      gap: v(2),
    },
    rowMain: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: s(10),
    },
    leftCol: {
      flex: 1,
      gap: v(8),
      minWidth: 0,
    },
    rightCol: {
      alignItems: 'flex-end',
      justifyContent: 'center',
      gap: v(8),
      flexShrink: 0,
    },
    nameRow: {
      flexDirection: 'row',
      alignItems: 'center',
      minWidth: 0,
    },
    customerName: {
      flexShrink: 1,
      fontFamily: GTFonts.semibold,
      color: colors.text,
      fontSize: fs(16),
      lineHeight: fs(18),
    },
    productCount: {
      marginLeft: 3,
      fontFamily: GTFonts.regular,
      color: colors.textSecondary,
      fontSize: fs(12),
      lineHeight: fs(18),
    },
    statusChip: {
      borderWidth: borderWidth(1),
      borderRadius: s(999),
      paddingHorizontal: s(8),
      paddingVertical: v(2),
    },
    statusText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(10),
      lineHeight: fs(12),
    },
    metaRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
    },
    metaPill: {
      paddingHorizontal: s(8),
      paddingVertical: v(2),
      borderRadius: s(999),
      backgroundColor: withOpacity(colors.text, 0.06),
      borderWidth: borderWidth(1),
      borderColor: withOpacity(colors.text, 0.08),
    },
    metaPillText: {
      fontFamily: GTFonts.semibold,
      color: colors.textSecondary,
      fontSize: fs(11),
      lineHeight: fs(12),
    },
    metaText: {
      fontFamily: GTFonts.regular,
      color: colors.text,
      fontSize: fs(11),
      lineHeight: fs(15),
    },
    rightBadges: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      flexWrap: 'nowrap',
      gap: s(6),
    },
    amountText: {
      fontFamily: GTFonts.semibold,
      color: colors.text,
      fontSize: fs(16),
      lineHeight: fs(18),
    },
    paymentChip: {
      borderWidth: borderWidth(1),
      borderRadius: s(999),
      paddingHorizontal: s(8),
      paddingVertical: v(2),
      flexShrink: 1,
    },
    paymentText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(10),
      lineHeight: fs(12),
    },
    receiptText: {
      fontFamily: GTFonts.regular,
      color: colors.textSecondary,
      fontSize: fs(11),
      lineHeight: fs(16),
    },
  });

export default RecentTransactionsSection;
